import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import {
  ArrowRightAlt,
  Brush,
  CropSquare,
  PanToolAlt,
  Redo,
  ShowChart,
  TextFields,
  Undo,
  CircleOutlined,
  Add,
  Remove,
  FitScreen,
} from '@mui/icons-material';
import { useFabricAnnotationCanvas } from './useFabricAnnotationCanvas';

export type AssemblyImageAnnotationEditorHandle = {
  save: () => Promise<void>;
  saveAs: () => Promise<void>;
};

type Props = {
  imageUrl: string;
  onSave: (blob: Blob) => void | Promise<void>;
  onSaveAs: (blob: Blob) => void | Promise<void>;
  onReadyChange?: (ready: boolean) => void;
  canAnnotate?: boolean;
};

export const AssemblyImageAnnotationEditor = forwardRef<AssemblyImageAnnotationEditorHandle, Props>(
  function AssemblyImageAnnotationEditor(
    { imageUrl, onSave, onSaveAs, onReadyChange, canAnnotate = false },
    ref
  ) {
    const {
      setCanvasElement,
      isReady,
      loadError,
      activeTool,
      setTool,
      canUndo,
      canRedo,
      undo,
      redo,
      exportAsBlob,
      zoom,
      setViewportToFit,
      setZoomToCenter,
      annotationColor,
      setAnnotationColor,
    } = useFabricAnnotationCanvas({ imageUrl });

    useImperativeHandle(
      ref,
      () => ({
        save: async () => {
          await onSave(await exportAsBlob());
        },
        saveAs: async () => {
          await onSaveAs(await exportAsBlob());
        },
      }),
      [exportAsBlob, onSave, onSaveAs]
    );

    useEffect(() => {
      onReadyChange?.(isReady);
    }, [isReady, onReadyChange]);

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (!isReady) return;

        const isCtrlOrCmd = event.ctrlKey || event.metaKey;
        if (!isCtrlOrCmd) return;

        if (event.key.toLowerCase() === 'z') {
          event.preventDefault();
          if (event.shiftKey) {
            void redo();
          } else {
            void undo();
          }
        }
      };

      globalThis.addEventListener('keydown', handleKeyDown);
      return () => globalThis.removeEventListener('keydown', handleKeyDown);
    }, [isReady, redo, undo]);

    return (
      <Stack height="100%" width="100%" flex={1} minHeight={0} sx={{ flexDirection: 'column' }}>
        <Box sx={{ flex: 1, position: 'relative', minHeight: 0, width: '100%' }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              border: '1px solid',
              borderColor: 'primary.200',
              borderRadius: '0.6rem',
              backgroundColor: '#ffffff',
              overflow: 'hidden',
            }}
          >
            <canvas ref={setCanvasElement} data-testid="annotation-canvas" />
          </Box>

          {/* Right floating tool rail — annotation tools only (requires canAnnotate) */}
          {canAnnotate && (
            <Stack
              sx={{
                position: 'absolute',
                right: '1.6rem',
                top: '1.6rem',
                width: '3.2rem',
                borderRadius: '0.8rem',
                border: '1px solid',
                borderColor: 'rgba(255, 255, 255, 0.12)',
                backgroundColor: '#262626',
                alignItems: 'center',
                py: '0.6rem',
                gap: '0.2rem',
                '& .MuiIconButton-root': {
                  color: 'rgba(255, 255, 255, 0.75)',
                },
                '& .MuiIconButton-colorPrimary': {
                  color: 'primary.light',
                },
                '& .MuiIconButton-root.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.28)',
                },
              }}
            >
              <Tooltip title="Select" placement="left">
                <IconButton
                  size="small"
                  onClick={() => setTool('select')}
                  color={activeTool === 'select' ? 'primary' : 'default'}
                  disabled={!isReady}
                >
                  <PanToolAlt fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Pencil" placement="left">
                <IconButton
                  size="small"
                  onClick={() => setTool('pencil')}
                  color={activeTool === 'pencil' ? 'primary' : 'default'}
                  disabled={!isReady}
                >
                  <Brush fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Line" placement="left">
                <IconButton
                  size="small"
                  onClick={() => setTool('line')}
                  color={activeTool === 'line' ? 'primary' : 'default'}
                  disabled={!isReady}
                >
                  <ShowChart fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Arrow" placement="left">
                <IconButton
                  size="small"
                  onClick={() => setTool('arrow')}
                  color={activeTool === 'arrow' ? 'primary' : 'default'}
                  disabled={!isReady}
                >
                  <ArrowRightAlt fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rectangle" placement="left">
                <IconButton
                  size="small"
                  onClick={() => setTool('rect')}
                  color={activeTool === 'rect' ? 'primary' : 'default'}
                  disabled={!isReady}
                >
                  <CropSquare fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Ellipse" placement="left">
                <IconButton
                  size="small"
                  onClick={() => setTool('ellipse')}
                  color={activeTool === 'ellipse' ? 'primary' : 'default'}
                  disabled={!isReady}
                >
                  <CircleOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Text" placement="left">
                <IconButton
                  size="small"
                  onClick={() => setTool('text')}
                  color={activeTool === 'text' ? 'primary' : 'default'}
                  disabled={!isReady}
                >
                  <TextFields fontSize="small" />
                </IconButton>
              </Tooltip>
              <Box sx={{ height: '0.4rem' }} />
              <Tooltip title="Undo" placement="left">
                <span>
                  <IconButton size="small" onClick={() => undo()} disabled={!isReady || !canUndo}>
                    <Undo fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Redo" placement="left">
                <span>
                  <IconButton size="small" onClick={() => redo()} disabled={!isReady || !canRedo}>
                    <Redo fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Box sx={{ height: '0.4rem' }} />
              <Tooltip title="Color" placement="left">
                <Box
                  sx={{
                    width: '2.6rem',
                    height: '2.6rem',
                    borderRadius: '0.4rem',
                    border: '1px solid',
                    borderColor: 'rgba(255, 255, 255, 0.25)',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isReady ? 1 : 0.45,
                    flexShrink: 0,
                  }}
                >
                  <input
                    type="color"
                    value={annotationColor}
                    onChange={(event) => setAnnotationColor(event.target.value)}
                    disabled={!isReady}
                    aria-label="Annotation color"
                    data-testid="annotation-color-input"
                    style={{
                      width: '3.2rem',
                      height: '3.2rem',
                      padding: 0,
                      border: 'none',
                      cursor: isReady ? 'pointer' : 'not-allowed',
                    }}
                  />
                </Box>
              </Tooltip>
            </Stack>
          )}

          {/* Bottom-right zoom controls */}
          <Stack
            sx={{
              position: 'absolute',
              right: '1.6rem',
              bottom: '1.6rem',
              borderRadius: '0.8rem',
              border: '1px solid',
              borderColor: 'neutral.200',
              backgroundColor: '#ffffff',
              overflow: 'hidden',
            }}
          >
            <Tooltip title="Zoom in" placement="left">
              <span>
                <IconButton
                  size="small"
                  disabled={!isReady}
                  onClick={() => setZoomToCenter(zoom * 1.25)}
                >
                  <Add fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Zoom out" placement="left">
              <span>
                <IconButton
                  size="small"
                  disabled={!isReady}
                  onClick={() => setZoomToCenter(zoom / 1.25)}
                >
                  <Remove fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Fit" placement="left">
              <span>
                <IconButton size="small" disabled={!isReady} onClick={setViewportToFit}>
                  <FitScreen fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {loadError && (
            <Box
              sx={{
                position: 'absolute',
                left: '1.6rem',
                bottom: '1.6rem',
                color: 'error.main',
                fontSize: '1.2rem',
                backgroundColor: '#ffffff',
                border: '1px solid',
                borderColor: 'error.light',
                borderRadius: '0.6rem',
                px: '0.8rem',
                py: '0.4rem',
                maxWidth: '60%',
              }}
            >
              Failed to load image. {loadError}
            </Box>
          )}
        </Box>
      </Stack>
    );
  }
);
